// src/App.js
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import PrivateRoute from './components/PrivateRoute';
import { registerClientHooks } from './mergesafe/registerClientHooks';

// Core pages
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';

import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import InvoiceEdit from './pages/InvoiceEdit';
import InvoiceNew from './pages/InvoiceNew';

// 💳 Payments
import Payments from './pages/Payments';
import PaymentDetail from './pages/PaymentDetail';

// AI / Profile / Settings
import AIChatPage from './pages/AIChatPage';
import Profile from './pages/Profile';
import Settings from './pages/Settings';

// Quotes
import QuoteList from './pages/quote/QuoteList';
import QuoteDetails from './pages/quote/QuoteDetails';
import QuoteNew from './pages/quote/QuoteNew';
import QuoteEdit from './pages/quote/QuoteEdit';

// Unified Work Hub
import WorkPage from './pages/work/WorkPage';
import WorkDetails from './pages/work/WorkDetails';

// ✅ Requests
import Requests from './pages/Requests';
import RequestDetails from './pages/RequestDetail';

// Auth/email pages (code-split)
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const AcceptInvite = lazy(() => import('./pages/auth/AcceptInvite'));

// Public views (code-split)
const PublicQuoteView = lazy(() => import('./pages/public/PublicQuoteView'));
const PublicInvoiceView = lazy(() => import('./pages/public/PublicInvoiceView'));

// ✅ Public Request Widget (code-split)
// NOTE: If you saved the file elsewhere, adjust this import path accordingly.
const PublicRequestWidget = lazy(() => import('./pages/public/PublicRequestWidget'));

function AppShell() {
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden relative">
        <Topbar />
        <div className="p-6 flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function RouteLoader() {
  return <div className="p-6 text-gray-600">Loading…</div>;
}

export default function App() {
  useEffect(() => {
    const cleanup = registerClientHooks();
    return cleanup;
  }, []);

  return (
    <Router>
      <ToastContainer position="bottom-right" autoClose={3000} />

      <Routes>
        {/* Public Auth/Token routes */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<RouteLoader />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<RouteLoader />}>
              <Register />
            </Suspense>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<RouteLoader />}>
              <ForgotPassword />
            </Suspense>
          }
        />
        <Route
          path="/reset/:token"
          element={
            <Suspense fallback={<RouteLoader />}>
              <ResetPassword />
            </Suspense>
          }
        />
        <Route
          path="/verify-email/:token"
          element={
            <Suspense fallback={<RouteLoader />}>
              <VerifyEmail />
            </Suspense>
          }
        />
        <Route
          path="/invite/accept/:token"
          element={
            <Suspense fallback={<RouteLoader />}>
              <AcceptInvite />
            </Suspense>
          }
        />

        {/* Public quote & invoice views (no auth, minimal chrome) */}
        <Route
          path="/public/quote/:token"
          element={
            <Suspense fallback={<RouteLoader />}>
              <PublicQuoteView />
            </Suspense>
          }
        />
        <Route
          path="/public/invoice/:token"
          element={
            <Suspense fallback={<RouteLoader />}>
              <PublicInvoiceView />
            </Suspense>
          }
        />

        {/* ✅ Public Request Widget hosted page (embeddable) */}
        {/* Accepts either slug OR publicId as :key */}
        <Route
          path="/r/:key"
          element={
            <Suspense fallback={<RouteLoader />}>
              <PublicRequestWidget />
            </Suspense>
          }
        />

        {/* Protected App */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <AppShell />
            </PrivateRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<Dashboard />} />

          {/* Clients */}
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientDetail />} />

          {/* Invoices */}
          <Route path="invoices" element={<Invoices />} />
          <Route path="invoices/new" element={<InvoiceNew />} />
          <Route path="invoices/:id/edit" element={<InvoiceEdit />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />

          {/* Payments */}
          <Route path="payments" element={<Payments />} />
          <Route path="payments/:id" element={<PaymentDetail />} />

          {/* Quotes */}
          <Route path="quotes" element={<QuoteList />} />
          <Route path="quotes/new" element={<QuoteNew />} />
          <Route path="quotes/:id/edit" element={<QuoteEdit />} />
          <Route path="quotes/:id" element={<QuoteDetails />} />

          {/* ✅ Requests */}
          <Route path="requests" element={<Requests />} />
          <Route path="requests/:id" element={<RequestDetails />} />

          {/* Unified Work Hub */}
          <Route path="work" element={<WorkPage />} />
          <Route path="work/tasks" element={<WorkPage />} />
          <Route path="work/jobs" element={<WorkPage />} />
          <Route path="work/schedule" element={<WorkPage />} />
          {/* Details page (accepts either a database id or workNumber) */}
          <Route path="work/:workKey" element={<WorkDetails />} />

          {/* Backward-compat: old /tasks → new Work Hub */}
          <Route path="tasks" element={<Navigate to="/work/tasks" replace />} />

          {/* AI */}
          <Route path="ai" element={<AIChatPage />} />

          {/* Profile / Settings */}
          <Route path="profile" element={<Profile />} />
          <Route path="users" element={<Navigate to="/settings" replace />} />
          <Route path="settings" element={<Settings />} />

          {/* 404 → Home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>

        {/* Catch-all for unknown public routes → login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}